import {
  ANNOTATION_EDIT_URL,
  ANNOTATION_SOURCE_LOCATION,
  Entity,
} from '@backstage/catalog-model';
import { CheckResult } from '@backstage-community/plugin-tech-insights-common';

/** Why a check failed, and where to go to fix it. */
export type FailureReason = {
  reason: string;
  where?: { title: string; url: string };
};

type Fact = (name: string) => unknown;

const ANSWER_LABELS: Record<string, string> = {
  true: 'Yes',
  false: 'No',
  unknown: "Don't know",
  'not-set': 'not answered',
};

/** Lists the quiz answers that keep a check from passing. */
const answersNotYes = (fact: Fact, answers: [string, string][]) => {
  const open = answers
    .filter(([name]) => fact(name) !== 'true')
    .map(
      ([name, label]) =>
        `${label}: ${ANSWER_LABELS[String(fact(name))] ?? fact(name)}`,
    );
  return open.join(' · ');
};

const versionReason = (tool: string, minimum: string, pin: unknown) => {
  if (pin === 'unpinned') return `No ${tool} version is pinned.`;
  if (pin === 'unknown')
    return `The ${tool} pin is not a version Backstage can compare.`;
  return `Pins ${tool} ${pin}; needs ${minimum} or newer.`;
};

/** One sentence per check, built from the facts the check was judged on. */
const REASONS: Record<string, (fact: Fact) => string> = {
  hasGithubDescription: () => 'The GitHub repository has no description.',
  hasGithubReadme: () => 'No README in the repository.',
  pnpmSupportsMinimumReleaseAge: fact =>
    versionReason('pnpm', '10.16.0', fact('pnpmVersion')),
  nodeVersionSupported: fact =>
    versionReason('Node', '20', fact('nodeVersion')),
  hasContributingGuide: () =>
    'No CONTRIBUTING.md at the root, in .github/ or in docs/.',
  hasCodeowners: () =>
    'No CODEOWNERS file at the root, in .github/ or in docs/.',
  hasAgentsMd: () => 'No agents.md at the repository root.',
  hasIncidentRunbook: () =>
    'No Markdown file with "runbook" in its name under docs/.',
  hasArchitectureDiagram: () => 'No architecture or HLA diagram under docs/.',
  usesAtgConfig: () => '@atg-digital/config is not in package.json.',
  usesAtgEslintConfig: () =>
    '@atg-digital/eslint-config is not in package.json.',
  usesAtgServerLogger: () =>
    '@atg-digital/server-logger-library is not in package.json.',
  hasNoSnyk: () => 'A workflow or a .snyk file still uses Snyk.',
  hasNoCommittedEnvFile: () =>
    'An environment file such as .env.production is committed.',
  hasNoServerlessFramework: () =>
    'A serverless.yml or serverless.ts is still in the repository.',
  usesReusableWorkflows: () =>
    'No workflow uses atg-digital/reusable-workflows.',
  hasCodecov: () => 'No codecov.yml, and no workflow uploads to Codecov.',
  usesInfrastructureAsCode: () =>
    'No Terraform, Serverless Framework, SST or CDK definitions found.',
  hasPagerDuty: () =>
    'The catalog entry has no pagerduty.com/service-id annotation.',
  hasNewRelic: () =>
    'The catalog entry has no newrelic.com/entity-guid or dashboard-guid annotation, and no newrelic.com/no-dashboard reason.',
  declaresPrivacy: fact =>
    `Not answered: ${[
      ['processesPii', 'whether it processes PII'],
      ['processesPayments', 'whether it processes payments'],
    ]
      .filter(([name]) => fact(name) === 'not-set')
      .map(([, label]) => label)
      .join(' or ')}.`,
  piiHandledSafely: fact =>
    answersNotYes(fact, [
      ['piiMinimised', 'Data minimised'],
      ['piiRetentionPolicy', 'Retention policy'],
      ['encryptionAtRest', 'Encrypted at rest'],
      ['encryptionInTransit', 'Encrypted in transit'],
    ]),
  noSensitiveDataInLogs: fact =>
    answersNotYes(fact, [['piiFreeLogs', 'Kept out of logs']]),
  dependenciesDocumented: fact =>
    answersNotYes(fact, [
      ['dependenciesDocumented', 'Dependencies documented'],
    ]),
  gdprFindingsResolved: fact =>
    fact('gdprRating') === 'not-set'
      ? 'GDPR review rating: not answered'
      : `GDPR rating ${fact('gdprRating')}, findings ${String(
          fact('gdprFindingsAddressed'),
        ).replace('not-set', 'not answered')}`,
  hasServiceTier: () => 'No tier:N tag or tier answer.',
  riskAssessmentComplete: fact =>
    `Unanswered: ${fact('riskAssessmentMissing')}.`,
};

/** Where in the repository each repository check looks. */
const REPO_PATHS: Record<string, [string, string]> = {
  hasGithubReadme: ['', 'repository'],
  hasGithubDescription: ['', 'repository'],
  hasContributingGuide: ['', 'repository'],
  hasCodeowners: ['', 'repository'],
  hasAgentsMd: ['', 'repository'],
  usesInfrastructureAsCode: ['', 'repository'],
  hasIncidentRunbook: ['docs', 'docs/'],
  hasArchitectureDiagram: ['docs', 'docs/'],
  pnpmSupportsMinimumReleaseAge: ['package.json', 'package.json'],
  nodeVersionSupported: ['package.json', 'package.json'],
  usesAtgConfig: ['package.json', 'package.json'],
  usesAtgEslintConfig: ['package.json', 'package.json'],
  usesAtgServerLogger: ['package.json', 'package.json'],
  hasNoSnyk: ['.github/workflows', 'workflows'],
  hasNoCommittedEnvFile: ['', 'repository'],
  hasNoServerlessFramework: ['', 'repository'],
  usesReusableWorkflows: ['.github/workflows', 'workflows'],
  hasCodecov: ['.github/workflows', 'workflows'],
};
const CATALOG_CHECKS = new Set(['hasPagerDuty', 'hasNewRelic']);

const whereToFix = (checkId: string, entity: Entity) => {
  const annotations = entity.metadata.annotations ?? {};
  if (CATALOG_CHECKS.has(checkId) && annotations[ANNOTATION_EDIT_URL]) {
    return {
      title: 'Edit catalog entry',
      url: annotations[ANNOTATION_EDIT_URL],
    };
  }
  const source = annotations[ANNOTATION_SOURCE_LOCATION]?.replace(/^url:/, '');
  const path = REPO_PATHS[checkId];
  if (!source || !path) return undefined;
  return { title: `Open ${path[1]}`, url: new URL(path[0], source).toString() };
};

/** Explains a failed check from its own facts, or undefined if it cannot. */
export const failureReason = (
  result: CheckResult,
  entity: Entity,
): FailureReason | undefined => {
  const explain = REASONS[result.check.id];
  if (!explain) return undefined;
  const fact: Fact = name => result.facts[name]?.value;
  return { reason: explain(fact), where: whereToFix(result.check.id, entity) };
};
